import React, { useEffect } from 'react';

import { JabraLoader } from '@gn-audio/jss-components';

import { HearingTestProvider, useHearingTest } from 'composables/HearingTest/HearingTestContext';
import { EarIntroStep } from 'composables/HearingTest/steps/EarIntroStep';
import { FrequencyTestStep } from 'composables/HearingTest/steps/FrequencyTestStep';
import GeneralQuestionsStep from 'composables/HearingTest/steps/GeneralQuestionsStep';
import { IntroductionStep } from 'composables/HearingTest/steps/IntroductionStep';
import LifestyleQuestions1Step from 'composables/HearingTest/steps/LifestyleQuestions1Step';
import LifestyleQuestions2Step from 'composables/HearingTest/steps/LifestyleQuestions2Step';
import { ResultsStep } from 'composables/HearingTest/steps/ResultsStep';
import { SoundCheckStep } from 'composables/HearingTest/steps/SoundCheckStep';
import { FREQUENCIES, STEP_NAMES } from 'composables/HearingTest/types/HearingTestTypes';
import { HearingTestProps } from 'composables/HearingTest/types/SitecoreFields';

const HearingTestContent: React.FC = () => {
  const { state, isLoading } = useHearingTest();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [state.currentStep]);

  const renderStep = () => {
    switch (state.currentStep) {
      case STEP_NAMES.INTRODUCTION:
        return <IntroductionStep />;
      case STEP_NAMES.GENERAL_QUESTIONS:
        return <GeneralQuestionsStep />;
      case STEP_NAMES.LIFESTYLE_QUESTIONS_1:
        return <LifestyleQuestions1Step />;
      case STEP_NAMES.LIFESTYLE_QUESTIONS_2:
        return <LifestyleQuestions2Step />;
      case STEP_NAMES.SOUND_CHECK:
        return <SoundCheckStep />;

      case STEP_NAMES.LEFT_EAR_INTRO:
        return <EarIntroStep ear="left" />;
      case STEP_NAMES.LEFT_500HZ:
        return <FrequencyTestStep frequency={FREQUENCIES.HZ_500} ear="left" />;
      case STEP_NAMES.LEFT_1000HZ:
        return <FrequencyTestStep frequency={FREQUENCIES.HZ_1000} ear="left" />;
      case STEP_NAMES.LEFT_2000HZ:
        return <FrequencyTestStep frequency={FREQUENCIES.HZ_2000} ear="left" />;
      case STEP_NAMES.LEFT_4000HZ:
        return <FrequencyTestStep frequency={FREQUENCIES.HZ_4000} ear="left" />;

      case STEP_NAMES.RIGHT_EAR_INTRO:
        return <EarIntroStep ear="right" />;
      case STEP_NAMES.RIGHT_500HZ:
        return <FrequencyTestStep frequency={FREQUENCIES.HZ_500} ear="right" />;
      case STEP_NAMES.RIGHT_1000HZ:
        return <FrequencyTestStep frequency={FREQUENCIES.HZ_1000} ear="right" />;
      case STEP_NAMES.RIGHT_2000HZ:
        return <FrequencyTestStep frequency={FREQUENCIES.HZ_2000} ear="right" />;
      case STEP_NAMES.RIGHT_4000HZ:
        return <FrequencyTestStep frequency={FREQUENCIES.HZ_4000} ear="right" />;

      case STEP_NAMES.RESULTS:
        return <ResultsStep />;

      default:
        return (
          <div className="mx-auto max-w-2xl p-6 text-center">
            <h1 className="mb-4 text-3xl font-bold text-gray-900">
              Hearing Test - Step {state.currentStep + 1}
            </h1>
            <p className="mb-8 text-lg text-gray-600">This step is under development.</p>
          </div>
        );
    }
  };

  return (
    <div className="relative mx-auto mb-20 mt-12 w-full max-w-4xl px-2">
      {renderStep()}
      {isLoading ? (
        <div className="absolute inset-0 bg-white/80">
          <JabraLoader />
        </div>
      ) : null}
    </div>
  );
};

const HearingTest: React.FC<HearingTestProps> = ({ rendering }) => {
  if (
    !rendering?.fields ||
    !rendering.fields.goodHearingMaxScore?.value ||
    !rendering.fields.moderateLossMaxScore?.value ||
    !rendering.fields.productTiles ||
    !Array.isArray(rendering.fields.productTiles)
  ) {
    return (
      <div className="hearing-test-error">
        <p>Hearing Test configuration not found. Please configure the component in Sitecore.</p>
      </div>
    );
  }

  return (
    <HearingTestProvider sitecoreFields={rendering.fields}>
      <HearingTestContent />
    </HearingTestProvider>
  );
};

export default HearingTest;
